import { initialFilterObject, useFilterStore } from '@store/index';
import { ReactElement, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getDatabase } from '@rxdb/config/databaseConfig';
import { ClubDetails } from '@services/hooks/enduser-details';
import { MultiDateRangePicker } from '@components/common';
import { Dropdown, MultiselectDropdown } from '../formControl';
import { useFormik } from 'formik';
import { SUPERVISOR_ALLOWED_CLASS } from '@config/constant';
import { MemberListDropDown } from '@components/common/formControl/dropdown/components';
import { useGetMemberByFilterOptions } from '@services/hooks/endusers';
import { useUserRoleType } from '@hooks/useUserRoleType';
import { CMDIDropdownRBDMItem } from '@services/hooks/endusers/cmdiDropdownRBDM';
import { useRBDMClubDropdownQuery } from '@services/hooks/meetings/useRBDMClubDropdownQuery';
import { RBDMClubDropdownItem } from '@services/hooks/meetings';
import { useComponentLevelAccessCheck } from '../../../authGuard/hooks/useComponentLevelAccessCheck';
export interface Filter {
	setFilterOpen: () => void;
}
export interface FilterObject {
	visitStatus: string;
	kycStatus: string;
	selectedValues: string[];
	secondarySelectedValues: string[][];
	club: string;
	club_data: { id: number; value: string }[];
	multiSelect: { id: number; value: string }[];
	filter_date: [Date | null, Date | null];
	largeContractor: string;
	ftqVisitStatus: string;
	mdicode: string;
	mdicode_data: { id: string; value: string }[];
	rigidGluePotential: boolean;
}

export const Filter = (props: Filter): ReactElement => {
	const userRole = useUserRoleType();
	const { setFilterOpen } = props;
	const { pathname } = useLocation();
	const { filterObject, updateFilterObject } = useFilterStore((state) => state);
	const useRigidAllowFilter = userRole === 'BDE' || userRole === 'BDE SE' || userRole === 'SS';

	// Check SSM filter permission based on current route
	const isAllEndUserPage = pathname === '/rbdm/end-users/all' || pathname === '/end-users/all';
	const ssmPermissionKey = isAllEndUserPage ? 'smm-filter-end-user' : 'smm-filter';
	const isSMMFilterEnable = useComponentLevelAccessCheck(ssmPermissionKey);
	const initialValues: FilterObject = {
		visitStatus: '',
		kycStatus: '',
		selectedValues: [],
		secondarySelectedValues: new Array(filterObject.checkSecondary.length).fill([]),
		club: '',
		club_data: [],
		multiSelect: [],
		filter_date: [null, null],
		largeContractor: '',
		ftqVisitStatus: '',
		mdicode: '',
		mdicode_data: [],
		rigidGluePotential: false,
	};
	const formik = useFormik({
		initialValues,
		onSubmit: () => {},
	});

	const handleChildClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
		e.stopPropagation();
	};

	const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>, value: string): void => {
		const isChecked = e.target.checked;
		const updatedValues = isChecked
			? [...formik.values.selectedValues, value]
			: formik.values.selectedValues.filter((item) => item !== value);
		formik.setFieldValue('selectedValues', updatedValues);
	};

	const handleVisitStatusChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		formik.setFieldValue('visitStatus', e.target.value);
	};
	const handleKycStatusChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		formik.setFieldValue('kycStatus', e.target.value);
	};
	const { result } = useGetMemberByFilterOptions({
		searchValue: formik?.values?.largeContractor?.split(' - ')[1],
		contractorclass: [],
	});

	// RBDM Club Dropdown API call - triggered when MDI code is selected
	const {
		data: rbdmClubData,
		isLoading: rbdmClubLoading,
		isFetching: rbdmClubFetching,
	} = useRBDMClubDropdownQuery(formik.values.mdicode);

	const handleSubmit = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
		e.preventDefault();

		// Remove SSM from selectedValues and data array if permission is enabled before saving
		let toggleDataToSave = formik.values.selectedValues;
		let customToggleData = filterObject.customToggle.data;

		if (isSMMFilterEnable) {
			toggleDataToSave = toggleDataToSave.filter((item) => item !== 'SSM');
			customToggleData = customToggleData.filter((item) => item.value !== 'SSM');
		}

		updateFilterObject({
			...filterObject,
			kycStatus: { ...filterObject.kycStatus, kycStatus: formik.values.kycStatus },
			visitStatus: { ...filterObject.visitStatus, visitStatus: formik.values.visitStatus },
			customToggle: {
				...filterObject.customToggle,
				data: customToggleData,
				toggleData: toggleDataToSave,
			},
			checkSecondary: filterObject.checkSecondary.map((item, index) => {
				return {
					...item,
					secondaryData: formik.values.secondarySelectedValues[index],
				};
			}),
			customSelect: { ...filterObject.customSelect, customData: formik.values.multiSelect },
			dateOfVisit: {
				...filterObject.dateOfVisit,
				startDate: formik.values.filter_date[0],
				endDate: formik.values.filter_date[1],
			},
			singleDropDown: { ...filterObject.singleDropDown, dropDownData: formik.values.club },
			largeContractor: {
				...filterObject.largeContractor,
				dropDownData: formik.values.largeContractor,
				value: result[0]?._data?.membershipno,
			},
			ftqVisitStatus: { ...filterObject.ftqVisitStatus, ftqVisitStatus: formik.values.ftqVisitStatus },
			mdicodeDropdown: {
				...filterObject.mdicodeDropdown,
				dropDownData: formik.values.mdicode,
			},
			rigidGluePotential: {
				flagged: filterObject.rigidGluePotential?.flagged ?? false,
				enabled: formik.values.rigidGluePotential,
			},
		});
		setFilterOpen();
	};

	const getDataFromIndexDb = async (): Promise<void> => {
		try {
			const db = await getDatabase();
			const result = await db.club.find().exec();
			const resultRBDM = await db.rbdm_club.find().exec();
			await formik.setFieldValue('club_data', []);
			if (result?.length > 0 || resultRBDM?.length > 0) {
				const tempData: ClubDetails[] = [...result, ...resultRBDM].map((ele: { _data: ClubDetails[] }) => ele?._data);
				await formik.setFieldValue(
					'club_data',
					tempData?.map((item) => {
						return { id: item.id, value: item.name };
					})
				);
			}
		} catch (error) {
			await formik.setFieldValue('club_data', []);
		}
	};

	const getDataFromIndexDbCMDIDropdown = async (): Promise<void> => {
		try {
			const db = await getDatabase();
			const result = await db.mdi_code_list.find().exec();
			if (result?.length > 0) {
				const tempData: CMDIDropdownRBDMItem[] = result.map((ele: { _data: CMDIDropdownRBDMItem[] }) => ele?._data);
				await formik.setFieldValue(
					'mdicode_data',
					tempData?.map((item) => {
						return { id: item.sfid, value: item.mdicode_c };
					})
				);
			} else {
				await formik.setFieldValue('mdicode_data', []);
			}
			// Set mdicode value after loading the data to ensure it's not overwritten
			await formik.setFieldValue('mdicode', filterObject.mdicodeDropdown.dropDownData);
		} catch (error) {
			await formik.setFieldValue('mdicode_data', []);
			// Set mdicode value even on error to preserve the selected value
			await formik.setFieldValue('mdicode', filterObject.mdicodeDropdown.dropDownData);
		}
	};

	useEffect(() => {
		getDataFromIndexDb();
		getDataFromIndexDbCMDIDropdown();

		// Handle SSM filter restoration - ensure SSM is in/out of data array based on permission
		// SSM should show when permission is FALSE, hide when permission is TRUE
		const hasSSMInData = filterObject.customToggle.data.some((item) => item.value === 'SSM');
		const hasSSMInToggleData = filterObject.customToggle.toggleData.includes('SSM');

		let updatedFilterObject = { ...filterObject };

		// Update data array based on permission
		if (isSMMFilterEnable && hasSSMInData) {
			// Permission enabled but SSM in data - remove it (SSM should not show when permission is enabled)
			updatedFilterObject = {
				...updatedFilterObject,
				customToggle: {
					...updatedFilterObject.customToggle,
					data: updatedFilterObject.customToggle.data.filter((item) => item.value !== 'SSM'),
					toggleData: updatedFilterObject.customToggle.toggleData.filter((item) => item !== 'SSM'),
				},
			};
			// Update the store with corrected filter
			updateFilterObject(updatedFilterObject);
		} else if (!isSMMFilterEnable && !hasSSMInData) {
			// Permission disabled but SSM not in data - add it (SSM should show when permission is disabled)
			updatedFilterObject = {
				...updatedFilterObject,
				customToggle: {
					...updatedFilterObject.customToggle,
					data: [...updatedFilterObject.customToggle.data, { value: 'SSM' }],
				},
			};
			// Update the store with corrected filter
			updateFilterObject(updatedFilterObject);
		} else if (isSMMFilterEnable && hasSSMInToggleData) {
			// Permission enabled but SSM is selected - remove from toggleData
			updatedFilterObject = {
				...updatedFilterObject,
				customToggle: {
					...updatedFilterObject.customToggle,
					toggleData: updatedFilterObject.customToggle.toggleData.filter((item) => item !== 'SSM'),
				},
			};
			// Update the store with corrected filter
			updateFilterObject(updatedFilterObject);
		}

		formik.setFieldValue('visitStatus', updatedFilterObject.visitStatus.visitStatus);
		formik.setFieldValue('kycStatus', updatedFilterObject.kycStatus.kycStatus);

		// Remove SSM from selectedValues if permission is enabled (SSM should only be selectable when permission is disabled)
		let selectedValues = updatedFilterObject.customToggle.toggleData;
		if (isSMMFilterEnable) {
			selectedValues = selectedValues.filter((item) => item !== 'SSM');
		}
		formik.setFieldValue('selectedValues', selectedValues);

		formik.setFieldValue(
			'secondarySelectedValues',
			updatedFilterObject.checkSecondary.map((item) => item.secondaryData)
		);
		formik.setFieldValue('multiSelect', updatedFilterObject.customSelect.customData);
		formik.setFieldValue('dateOfVisit', {
			startDate: updatedFilterObject.dateOfVisit.startDate,
			endDate: updatedFilterObject.dateOfVisit.endDate,
		});
		formik.setFieldValue('club', updatedFilterObject.singleDropDown.dropDownData);
		formik.setFieldValue('filter_date', [
			updatedFilterObject.dateOfVisit.startDate,
			updatedFilterObject.dateOfVisit.endDate,
		]);
		formik.setFieldValue('largeContractor', updatedFilterObject.largeContractor.dropDownData);
		formik.setFieldValue('ftqVisitStatus', updatedFilterObject.ftqVisitStatus.ftqVisitStatus);
		formik.setFieldValue('rigidGluePotential', updatedFilterObject.rigidGluePotential?.enabled ?? false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterObject, isSMMFilterEnable]);

	// Function to get club options based on MDI code selection
	const getClubOptions = (): { id: number; value: string }[] => {
		if (formik.values.mdicode && rbdmClubData && rbdmClubData.length > 0) {
			const apiClubOptions: { id: number; value: string }[] = [];

			(rbdmClubData as unknown as RBDMClubDropdownItem[]).forEach((club) => {
				apiClubOptions.push({
					id: club.id,
					value: club.name,
				});
			});

			return apiClubOptions;
		}
		return formik.values.club_data;
	};

	useEffect(() => {
		if (formik.values.mdicode) {
			formik.setFieldValue('club', '');
			formik.setFieldValue('multiSelect', []);
		}
	}, [formik.values.mdicode]);

	const handleFilterResetHandler = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
		e.preventDefault();
		setFilterOpen();
		updateFilterObject({
			...initialFilterObject,
			dateOfVisit: { flagged: filterObject.dateOfVisit.flagged, startDate: null, endDate: null },
			customToggle: {
				flagged: filterObject.customToggle.flagged,
				data: filterObject.customToggle.data,
				toggleData: [],
			},
			visitStatus: { flagged: filterObject.visitStatus.flagged, visitStatus: '' },
			checkSecondary: filterObject.checkSecondary.map((item) => ({
				flagged: item.flagged,
				name: item.name,
				data: item.data,
				secondaryData: [],
			})),
			customSelect: { flagged: filterObject.customSelect.flagged, customData: [] },
			singleDropDown: {
				flagged: filterObject.singleDropDown.flagged,
				name: filterObject.singleDropDown.name,
				data: filterObject.singleDropDown.data,
				dropDownData: '',
			},
			kycStatus: { flagged: filterObject.kycStatus.flagged, kycStatus: '' },
			largeContractor: {
				flagged: filterObject.largeContractor.flagged,
				dropDownData: '',
				name: '',
				value: '',
			},
			ftqVisitStatus: { flagged: filterObject.ftqVisitStatus.flagged, ftqVisitStatus: '' },
			mdicodeDropdown: {
				flagged: filterObject.mdicodeDropdown.flagged,
				name: filterObject.mdicodeDropdown.name,
				data: filterObject.mdicodeDropdown.data,
				dropDownData: '',
			},
			rigidGluePotential: {
				flagged: filterObject.rigidGluePotential?.flagged ?? false,
				enabled: false,
			},
		});
	};

	useEffect(() => {
		const ele = document.getElementById('filter-main-wrapper') as HTMLElement;
		const overlay = document.getElementById('overlay') as HTMLElement;
		document.body.classList.add('overflow-hidden');
		overlay.classList.add('show');

		if (ele.className.includes('show')) {
			ele.classList.remove('show');
		}

		setTimeout(() => {
			ele.classList.add('show');
		}, 0);
	}, []);

	const handleItemClick = (index: number, value: string): void => {
		const toggleValueInArray = (array: string[], value: string): string[] => {
			if (array.includes(value)) {
				return array.filter((item) => item !== value);
			}
			return [...array, value];
		};

		const newState = formik.values.secondarySelectedValues.map((nestedArray, i) => {
			if (i === index) {
				return toggleValueInArray(nestedArray, value);
			}
			return nestedArray;
		});

		formik.setFieldValue('secondarySelectedValues', newState);
	};

	const isAllStateFilled = (): boolean => {
		return !(
			formik.values.visitStatus ||
			formik.values.kycStatus ||
			formik.values.selectedValues.length ||
			formik.values.secondarySelectedValues.some((item) => item.length) ||
			formik.values.club.length ||
			formik.values.filter_date.every((item) => item?.toString().length) ||
			formik.values.multiSelect.length ||
			formik.values.ftqVisitStatus ||
			formik.values.mdicode ||
			formik.values.rigidGluePotential
		);
	};

	const handleFTQVisitStatusChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		formik.setFieldValue('ftqVisitStatus', e.target.value);
	};

	return (
		<div
			role="button"
			tabIndex={0}
			className="filter-drawer-wrapper filter-contractor-drawer"
			id="filter-main-wrapper"
			onClick={setFilterOpen}
		>
			<div className="container">
				<button
					type="button"
					aria-label="filter popup"
					className="filter-sort-icon icon-filter"
					onClick={setFilterOpen}
				></button>
				<div
					role="button"
					tabIndex={0}
					className="filter-drawer-inner-wrapper filter"
					onClick={(e) => handleChildClick(e)}
				>
					<div className="filter-title">
						<p className="h2">Filter</p>
						<a href={pathname} aria-label="Clear All" onClick={handleFilterResetHandler} hidden={isAllStateFilled()}>
							Clear All
						</a>
					</div>
					<form>
						<div className="form-fieldset row">
							{userRole === 'RBDM' && (
								<div className="d-col">
									<span className="form-title h4">
										<span className="icon-shield"></span>
										{filterObject.mdicodeDropdown.name}
									</span>
									<Dropdown
										name="mdicode"
										id="mdicode"
										title={formik.values.mdicode}
										options={formik.values.mdicode_data}
										setFieldValue={formik.setFieldValue}
									/>
								</div>
							)}
							{filterObject.visitStatus.flagged && (
								<div className="d-col">
									<span className="form-title h4">
										<span className="icon-hammer"></span> Visit Status (MTQ)
									</span>
									<ul className="custom-check primary">
										<li className="custom-check-item">
											<label>
												<input
													type="radio"
													name="visitStatus"
													value="Visited"
													checked={formik.values.visitStatus === 'Visited'}
													onChange={handleVisitStatusChange}
												/>
												<span className="h4">Visited</span>
											</label>
										</li>
										<li className="custom-check-item">
											<label>
												<input
													type="radio"
													name="visitStatus"
													value="Not-Visited"
													checked={formik.values.visitStatus === 'Not-Visited'}
													onChange={handleVisitStatusChange}
												/>
												<span className="h4">Not-Visited</span>
											</label>
										</li>
									</ul>
								</div>
							)}
							{filterObject.ftqVisitStatus.flagged && (
								<div className="d-col">
									<span className="form-title h4">
										<span className="icon-hammer"></span> Visit Status (FTQ)
									</span>
									<ul className="custom-check primary">
										<li className="custom-check-item">
											<label>
												<input
													type="radio"
													name="ftqVisitStatus"
													value="Visited-FTQ"
													checked={formik.values.ftqVisitStatus === 'Visited-FTQ'}
													onChange={handleFTQVisitStatusChange}
												/>
												<span className="h4">Visited</span>
											</label>
										</li>
										<li className="custom-check-item">
											<label>
												<input
													type="radio"
													name="ftqVisitStatus"
													value="Not-Visited-FTQ"
													checked={formik.values.ftqVisitStatus === 'Not-Visited-FTQ'}
													onChange={handleFTQVisitStatusChange}
												/>
												<span className="h4">Not-Visited</span>
											</label>
										</li>
									</ul>
								</div>
							)}
							{filterObject.customToggle.flagged && (
								<div className="d-col">
									<ul className="custom-toggle-wrapper">
										{filterObject.customToggle.data
											.filter((item) => {
												if (item.value === 'SSM') {
													return !isSMMFilterEnable;
												}
												return true;
											})
											.map((item) => {
												return (
													<li key={item.value}>
														<span className="form-title h4">
															<span className="icon-user"></span>
															{item.value === 'SSM' ? 'SMM USER' : item.value}
														</span>
														<ul className="custom-toggle">
															<li className="custom-toggle-item">
																<input
																	type="checkbox"
																	id={`${item.value}`}
																	onChange={(e) => handleCheckboxChange(e, item.value)}
																	checked={formik.values.selectedValues.includes(item.value)}
																/>
																<label htmlFor={`${item.value}`}>
																	<span className="visually-hidden">{item.value}</span>
																</label>
															</li>
														</ul>
													</li>
												);
											})}
									</ul>
								</div>
							)}

							{filterObject.rigidGluePotential?.flagged && useRigidAllowFilter && (
								<div className="d-col">
									<ul className="custom-toggle-wrapper">
										<li key="Rigid Contractor glue potential" className="rigid-glue-potential-filter">
											<span className="form-title h4">
												<span className="icon-user"></span>
												Glue Consumption {'>='} 50
											</span>
											<ul className="custom-toggle">
												<li className="custom-toggle-item">
													<input
														type="checkbox"
														id="Rigid Contractor glue potential"
														onChange={(e) => formik.setFieldValue('rigidGluePotential', e.target.checked)}
														checked={formik.values.rigidGluePotential}
													/>
													<label htmlFor="Rigid Contractor glue potential">
														<span className="visually-hidden">Rigid Contractor glue potential</span>
													</label>
												</li>
											</ul>
										</li>
									</ul>
								</div>
							)}

							{filterObject?.largeContractor?.flagged && formik?.values?.selectedValues?.includes('Sub Contractor') && (
								<div className="d-col">
									<span className="form-title h4">
										<span className="icon-user"></span>
										{filterObject.largeContractor.name}
									</span>
									<ul className="custom-toggle-wrapper">
										<MemberListDropDown
											formik={formik}
											fieldName={'largeContractor'}
											contractorclass={SUPERVISOR_ALLOWED_CLASS}
											isMultiSelect={false}
										/>
									</ul>
								</div>
							)}

							{filterObject?.checkSecondary?.map(
								(item, index) =>
									item.flagged && (
										<div className="d-col" key={item.name}>
											<span className="form-title h4">
												<span className="icon-punch"></span>
												{item.name}
											</span>
											<ul className="custom-check secondary">
												{item.data.map((item) => {
													return (
														<li className="custom-check-item" key={item.value}>
															<label>
																<input
																	type="checkbox"
																	value={item.value}
																	onChange={() => handleItemClick(index, item.value)}
																	checked={formik.values.secondarySelectedValues[index].includes(item.value)}
																/>
																<span className="h4">
																	{item.value}
																	<span className="badge-parent">
																		<span className="green-badge"></span>
																	</span>
																</span>
															</label>
														</li>
													);
												})}
											</ul>
										</div>
									)
							)}

							{filterObject.kycStatus.flagged && (
								<div className="d-col">
									<span className="form-title h4">
										<span className="icon-hammer"></span>KYC Status
									</span>
									<ul className="custom-check primary">
										<li className="custom-check-item">
											<label>
												<input
													type="radio"
													value="Verified"
													checked={formik.values.kycStatus === 'Verified'}
													onChange={handleKycStatusChange}
												/>
												<span className="h4">Verified</span>
											</label>
										</li>
										<li className="custom-check-item">
											<label>
												<input
													type="radio"
													value="Not-Verified"
													checked={formik.values.kycStatus === 'Not-Verified'}
													onChange={handleKycStatusChange}
												/>
												<span className="h4">Not-Verified</span>
											</label>
										</li>
									</ul>
								</div>
							)}

							{filterObject.dateOfVisit.flagged && (
								<div className="d-col">
									<span className="form-title h4">
										<span className="icon-date"></span>Date of Creation
									</span>
									<MultiDateRangePicker
										id="filter_date"
										name="filter_date"
										value={formik.values.filter_date}
										formik={formik}
										maxDate={new Date()}
									/>
								</div>
							)}

							{filterObject.singleDropDown.flagged && (
								<div className="d-col">
									<span className="form-title h4">
										<span className="icon-shield"></span>
										{filterObject.singleDropDown.name}
									</span>

									<Dropdown
										name="club"
										id="club"
										title={formik.values.club}
										options={filterObject.singleDropDown.data}
										setFieldValue={formik.setFieldValue}
									/>
								</div>
							)}

							{filterObject.customSelect.flagged && (
								<div className="d-col">
									<span className="form-title h4">
										<span className="icon-shield"></span>Club
									</span>
									<MultiselectDropdown
										options={getClubOptions()}
										name={'multiSelect'}
										values={formik.values.multiSelect}
										setFieldValue={formik.setFieldValue}
										disabled={!!(userRole === 'RBDM' && formik.values.mdicode && (rbdmClubLoading || rbdmClubFetching))}
									/>
								</div>
							)}
						</div>
					</form>

					<div className="d-col submit">
						<button
							type="submit"
							className="btn btn-primary btn-medium filter-sort-submit-btn"
							onClick={(e) => {
								handleSubmit(e);
							}}
							disabled={isAllStateFilled()}
						>
							Submit
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};
